import React, { useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DataContainer from "../components/common/DataContainer/DataContainer";
import { useToast } from "../components/common/custom-component/ToastProvider";
import { useTableColumns } from "../hooks/useTableColumns";
import { useWorkflowAccess } from "../hooks/useWorkflowAccess";
import { getErrorMessage } from "helpers/errorUtils";

import { startWorkflowProcessInCompanyThunk, cloneWorkflowProcessThunk } from "store/thunks/workflowThunks";
import { Box, Typography, Divider, Alert } from "@mui/material";
import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import { logger } from "helpers/logger";
function WorkflowListPage() {
	// #region STATE
	const { error: ToastError, info: ToastInfo } = useToast();
	const navigate = useNavigate();
	const [t] = useTranslation();
	const dispatch = useDispatch();

	const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);

	// Rows are the workflows the user can see in the selected company (role grant + company
	// switch + active + current application), each with its usable permission codes. Cached
	// across visits and refreshed in the background - see useWorkflowAccess.
	const { workflows, canUse, isLoading, isRefreshing, error } = useWorkflowAccess();
	// #endregion

	// #region OPEN NEW PROCESS
	// Start and Clone both return the new process's first step ({ processInstanceId, taskId, formId,
	// data }); it opens in FormRendererPage. data pre-fills the form for a clone and is empty for a
	// start; nothing is saved until the user submits.
	const openFirstStep = useCallback((newProcess, title) => {
		if (!newProcess?.formId || !newProcess?.taskId) {
			// Started, but its first step isn't a form waiting for this user (e.g. a service step first).
			ToastInfo(t("The new process was started"));
			return;
		}

		navigate("/formRenderer", {
			state: {
				submissions: [
					{
						formId: newProcess.formId,
						submissionId: null,
						processInstanceId: newProcess.processInstanceId,
						taskId: newProcess.taskId,
						data: newProcess.data ?? {},
					},
				],
				taskId: newProcess.taskId,
				readOnly: false,
				title,
			},
		});
	}, [navigate, t, ToastInfo]);
	// #endregion

	// #region Start Process
	// The backend checks start_workflow and records the selected company on the process.
	const handleStartProcess = useCallback(async (processDefinitionKey) => {
		setIsLoadingBackdrop(true);
		try {
			const response = await dispatch(startWorkflowProcessInCompanyThunk({ processDefinitionKey })).unwrap();
			openFirstStep(response?.data, t("Start Workflow"));
		} catch (error) {
			logger.error("Failed to start process:", error);
			ToastError(getErrorMessage(error, t("Failed to start the process")));
		} finally {
			setIsLoadingBackdrop(false);
		}
	}, [dispatch, openFirstStep, t, ToastError]);
	// #endregion

	// #region CLONE
	// Clones the company's most recent process of this workflow: a new process pre-filled with its
	// answers to the fields marked clone = true in the form. The backend checks clone_workflow.
	const handleCloneLastWorkflow = useCallback(async (processDefinitionKey) => {
		setIsLoadingBackdrop(true);
		try {
			const response = await dispatch(cloneWorkflowProcessThunk({ processDefinitionKey })).unwrap();
			openFirstStep(response?.data, t("Clone Workflow"));
		} catch (error) {
			logger.error("Failed to clone workflow:", error);
			ToastError(getErrorMessage(error, t("Failed to clone the process")));
		} finally {
			setIsLoadingBackdrop(false);
		}
	}, [dispatch, openFirstStep, t, ToastError]);
	// #endregion

	// #region TABLE CONFIG

	const actions = useMemo(() => ({
		onStart: (row) => handleStartProcess(row.processDefinitionKey),
		onClone: (row) => handleCloneLastWorkflow(row.processDefinitionKey),
	}), [handleStartProcess, handleCloneLastWorkflow]);

	// Button rules in tbl_table_column_configs use the "hasWorkflowPermission" operator,
	// which calls canUse(permissionCode, row.processDefinitionKey).
	const context = useMemo(() => ({ canUse }), [canUse]);

	const { columns, isLoading: columnsLoading } = useTableColumns('WorkflowList', { actions, context });

	// #endregion

	// #region RENDER

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h3" color="primary">
					{t("Workflow List")}
				</Typography>
				{isRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading..." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("View and manage available workflow processes.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Rows or columns not there yet: nothing to draw, so the table area waits. */}
			{isLoading || columnsLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
					<Loader variant="dots" message="Loading..." />
				</Box>
			) : error && workflows.length === 0 ? (
				<Alert severity="error">{t("Failed to load workflows.")}</Alert>
			) : workflows.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle="No Workflows Available" emptyStateSubtitle="" />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer data={workflows} columns={columns} showRowNumbers={true} maxHeight={600} stickyHeader={true} />
				</Box>
			)}
			<LoadingBackdrop open={isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);

	// #endregion
}

export default WorkflowListPage;
